import { Component, OnInit } from '@angular/core';
import { User } from 'firebase/auth';
import Markdown from 'markdown-it';
import moment from 'moment';

import { AI_CONVERSATION_ROLES, CONVERSATION_PRIORITY_VALUES, DEFAULT_PRIORITY_TOKENS } from 'src/app/constants';
import {
  createPriorityValueToTokenMap,
  getIconForConversationPriority,
  isDev,
  isUrl as isURL,
} from 'src/app/utils/utils';
import { PriorityTokens } from 'src/models';
import {
  AGENT_CONFIDENCE_THRESHOLD,
  AIConversationMessage,
  AITesterAgentActivity,
  AITesterMessage,
} from 'src/models/ai-tester';
import { AITesterService, ASPECT_FORMATS, AgentMessage, AgentSelection } from 'src/services/ai-tester.service';
import { AuthService } from 'src/services/auth.service';
import { EmojisService } from 'src/services/emojis.service';
import { MessageService } from 'src/services/message.service';
import { TokenService } from 'src/services/token.service';

interface AgentComparisonMessage {
  message1: AITesterMessage;
  message2: AITesterMessage;
}

@Component({
  selector: 'app-agent-comparison-tester',
  templateUrl: './agent-comparison-tester.component.html',
  styleUrls: ['./agent-comparison-tester.component.scss'],
})
export class AgentComparisonTesterComponent implements OnInit {
  user: User;
  messageHistory1: AIConversationMessage[] = [];
  messageHistory2: AIConversationMessage[] = [];
  displayedMessages: (AITesterMessage | AgentComparisonMessage)[] = [];
  newMessage = '';
  isAgentThinking: boolean = false;
  isDrawerOpen: boolean = false;
  agents: AgentSelection[];
  selectedAgent1: string;
  selectedAgent2: string;
  markdown = new Markdown({
    breaks: true,
    linkify: true,
  });
  priorityTokens: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  ASPECT_FORMATS = ASPECT_FORMATS;

  // Make the imported isURL available to the template
  public isURL = isURL;

  constructor(
    private authService: AuthService,
    private aiTesterService: AITesterService,
    private messageService: MessageService,
    private tokenService: TokenService,
    private emojisService: EmojisService,
  ) {
    // bindings
    this.displayDateFrom = this.displayDateFrom.bind(this);
    this.intakeAgentMessageToAiTesterMessage = this.intakeAgentMessageToAiTesterMessage.bind(this);
  }

  async ngOnInit(): Promise<void> {
    await this.initConfig();
    await this.initializeCustomTokens();
  }

  async initConfig(): Promise<void> {
    // initialize user
    this.user = this.authService.user;
    // initialize agent list
    this.agents = await this.aiTesterService.listAgents();
    this.selectedAgent1 = this.agents[0]?.id;
    this.selectedAgent2 = this.agents[1]?.id;
  }

  async initializeCustomTokens() {
    return Promise.all([this.emojisService.loadEmojis(), this.initializePriorityTokens()]);
  }

  async initializePriorityTokens() {
    this.priorityTokens = (await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP']))['PRIORITY_LABEL_MAP'];
  }

  getObjectEntries(obj: any) {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  isDev() {
    return this.authService.isSuperAdmin || isDev();
  }

  isViewAgentThoughtsEnabled() {
    return this.authService.tenant.viewAgentThoughtsEnabled || this.isDev();
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  determineIfAiThoughtsShowLowConfidence(aiThoughts: AITesterAgentActivity[]): boolean {
    return aiThoughts.some((thought) => {
      return (
        thought.aspects &&
        ((thought.aspects.topic_selection_confidence != undefined &&
          thought.aspects.topic_selection_confidence < AGENT_CONFIDENCE_THRESHOLD) ||
          (thought.aspects.action_to_take_confidence != undefined &&
            thought.aspects.action_to_take_confidence < AGENT_CONFIDENCE_THRESHOLD))
      );
    });
  }

  async sendMessage() {
    if (this.newMessage.trim().length === 0) {
      return;
    }

    const message = {
      role: AI_CONVERSATION_ROLES.USER,
      content: this.newMessage.trim(),
      timestamp: this.displayDateFrom(new Date()),
    };
    // update state with user message
    this.updateMessageStatesForUser([message]);
    // clear input state, set loading state, and scroll down
    this.newMessage = '';
    this.isAgentThinking = true;
    this.scrollToBottom();
    // send message to endpoints
    const resp = await Promise.all([
      this.aiTesterService.sendMessage({
        agent: this.selectedAgent1,
        history: this.messageHistory1,
      }),
      this.aiTesterService.sendMessage({
        agent: this.selectedAgent2,
        history: this.messageHistory2,
      }),
    ]).catch((error) => {
      this.messageService.showError(error);
    });
    // reset loading state
    this.isAgentThinking = false;
    // update state with replies from agent
    if (resp) {
      this.updateMessageStatesForAgent(
        this.intakeAgentMessageToAiTesterMessage(resp[0][0]),
        this.intakeAgentMessageToAiTesterMessage(resp[1][0]),
      );
    }
  }

  scrollToBottom(): void {
    setTimeout(() => {
      const content = document.querySelector('ion-content');
      if (content) {
        content.scrollToBottom(300);
      }
    }, 100);
  }

  intakeAgentMessageToAiTesterMessage(intakeAgentMessage: AgentMessage): AITesterMessage {
    const formattedThoughts = intakeAgentMessage.formattedThoughts?.map((thought) => ({
      ...thought,
      showThoughtAspects: false,
    }));

    return {
      ...intakeAgentMessage,
      timestamp: this.displayDateFrom(new Date()),
      formattedThoughts,
      aiThoughtsShowLowConfidence: formattedThoughts
        ? this.determineIfAiThoughtsShowLowConfidence(formattedThoughts)
        : undefined,
    };
  }

  displayDateFrom(date: Date): string {
    return moment(date).fromNow();
  }

  updateMessageStatesForUser(newMessages: AITesterMessage[]): void {
    const filteredMessages = newMessages.filter((message) => message.content != null && message.content != '');

    this.displayedMessages.push(...filteredMessages);
    this.messageHistory1.push(
      ...filteredMessages.map(({ role, content }) => ({
        role,
        content,
      })),
    );
    this.messageHistory2.push(
      ...filteredMessages.map(({ role, content }) => ({
        role,
        content,
      })),
    );
  }

  updateMessageStatesForAgent(message1: AITesterMessage, message2: AITesterMessage): void {
    this.displayedMessages.push({ message1, message2 });
    this.messageHistory1.push({
      role: message1.role,
      content: message1.content,
    });
    this.messageHistory2.push({
      role: message2.role,
      content: message2.content,
    });
  }

  getIconForPriority(priority: string) {
    const priorityValue = CONVERSATION_PRIORITY_VALUES[priority];
    return getIconForConversationPriority(priorityValue);
  }

  getEmojiForPriority(priority: string) {
    return this.priorityTokens[priority]?.EMOJI;
  }

  getPriorityLabel(priority: string) {
    return this.priorityTokens[priority]?.LABEL;
  }

  isBot(role: string): boolean {
    return role === AI_CONVERSATION_ROLES.ASSISTANT;
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      // handle pressing enter to send
      // prevent the default Enter behavior of adding a newline
      event.preventDefault();
      // trigger the send message functionality
      this.sendMessage();
    } else if (event.key === 'Enter' && event.shiftKey) {
      // handle shift + enter for newline
      this.newMessage += '\n';
    }
  }

  convertToMarkdown(text: string): string {
    return this.markdown.render(text);
  }
}
